/* Movatic WMS — Frontend | CesarFilho 932048016
   components/CommandPalette.tsx — paleta de comandos (Ctrl+K), Direcao A P3b.
   Fonte: flattenLeafs(MENUS, user) (folhas navegaveis). Busca label+tcode (substring,
   case-insensitive). Salto direto por tcode exato. Overlay fixed (sem portal); tokens --vs-*. */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MENUS, type MenuItem } from '@/nav/menu'
import { flattenLeafs } from '@/nav/menu-utils'
import { useAuthStore } from '@/store/authStore'

type Props = { open: boolean; onClose: () => void }

export default function CommandPalette({ open, onClose }: Props) {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState(0)

  const leafs = useMemo(
    () => flattenLeafs(MENUS, user).filter((it) => it.path),
    [user],
  )

  const q = query.trim().toLowerCase()
  const results = useMemo(() => {
    if (!q) return leafs
    return leafs.filter(
      (it) =>
        it.label.toLowerCase().includes(q) ||
        (it.tcode?.toLowerCase().includes(q) ?? false),
    )
  }, [leafs, q])

  useEffect(() => {
    if (!open) return
    setQuery('')
    setSelected(0)
    const id = window.setTimeout(() => inputRef.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [open])

  useEffect(() => setSelected(0), [q])

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-idx="${selected}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [selected])

  if (!open) return null

  function go(item: MenuItem | undefined) {
    if (!item?.path) return
    navigate(item.path)
    onClose()
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelected((s) => Math.min(s + 1, Math.max(0, results.length - 1)))
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelected((s) => Math.max(s - 1, 0))
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      const exact = leafs.find((it) => it.tcode?.toLowerCase() === q) // salto direto por tcode exato
      go(exact ?? results[selected])
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onMouseDown={onClose}
    >
      <div
        className="mt-24 w-full max-w-xl overflow-hidden rounded-lg shadow-2xl"
        style={{
          background: 'var(--vs-panel-bg)',
          border: '1px solid var(--vs-panel-border)',
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Pesquisar modulo ou T-code (ex.: Scan, SC02)…"
          className="w-full bg-transparent px-4 py-3 text-sm outline-none"
          style={{
            color: 'var(--vs-text)',
            borderBottom: '1px solid var(--vs-panel-border)',
          }}
        />
        <div ref={listRef} className="max-h-80 overflow-y-auto py-1">
          {results.length === 0 ? (
            <div
              className="px-4 py-6 text-center text-sm"
              style={{ color: 'var(--vs-text-muted)' }}
            >
              Nada encontrado.
            </div>
          ) : (
            results.map((item, idx) => {
              const Icon = item.icon
              const active = idx === selected
              return (
                <div
                  key={item.id}
                  data-idx={idx}
                  onMouseEnter={() => setSelected(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    go(item)
                  }}
                  className="relative mx-2 grid cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-sm"
                  style={{
                    gridTemplateColumns: '22px 1fr auto',
                    color: active
                      ? 'var(--vs-nav-fg-hover)'
                      : 'var(--vs-nav-fg)',
                    background: active
                      ? 'var(--vs-nav-active-bg)'
                      : 'transparent',
                  }}
                >
                  {active && (
                    <span
                      aria-hidden
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 6,
                        bottom: 6,
                        width: 3,
                        borderRadius: '0 3px 3px 0',
                        background: 'var(--vs-nav-accent)',
                      }}
                    />
                  )}
                  {Icon ? (
                    <Icon
                      size={16}
                      className="shrink-0 opacity-80"
                      style={
                        active ? { color: 'var(--vs-nav-accent)' } : undefined
                      }
                    />
                  ) : (
                    <span />
                  )}
                  <span className="truncate">{item.label}</span>
                  {item.tcode && (
                    <span
                      className="shrink-0 rounded border px-1.5 py-0.5 font-mono text-[10px] tracking-tight"
                      style={{
                        color: active
                          ? 'var(--vs-nav-tcode-active)'
                          : 'var(--vs-nav-tcode)',
                        borderColor: active
                          ? 'var(--vs-nav-tcode-active)'
                          : 'var(--vs-nav-tcode-border)',
                      }}
                    >
                      {item.tcode}
                    </span>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
